// ============================================================
// BOOTH — tiny synthesized UI sounds (Web Audio, no asset files)
// ------------------------------------------------------------
// Two cues, both short and quiet, both built from oscillators at call time so
// there is nothing to download and nothing to cache:
//
//   tap   — a soft, bright tick for buttons and links. ~70 ms.
//   focus — a lower two-note "pop" for entering the search box. ~110 ms.
//
// The AudioContext is created lazily on the first call and resumed on every
// call, so as long as the caller is inside a user gesture (click, pointerdown,
// keydown) it plays on iOS Safari too, which refuses audio outside a gesture.
// Anything that fails (no Web Audio, blocked context) fails silently — sound
// is garnish, never a dependency.
//
// Client-only.
// ============================================================

let ctx: AudioContext | null = null

function getContext(): AudioContext | null {
  if (typeof window === "undefined") return null
  try {
    if (!ctx) {
      const Ctor = window.AudioContext || (window as any).webkitAudioContext
      if (!Ctor) return null
      ctx = new Ctor()
    }
    if (ctx.state === "suspended") void ctx.resume()
    return ctx
  } catch {
    return null
  }
}

interface Note {
  /** Start frequency (Hz). */
  from: number
  /** End frequency (Hz); the pitch glides there over the note's life. */
  to: number
  /** Note length in seconds. */
  dur: number
  /** Peak gain, 0–1. Keep low — these ride under the page, not over it. */
  peak: number
  /** Offset from now, seconds. */
  at?: number
  type?: OscillatorType
}

function play(notes: Note[]) {
  const ac = getContext()
  if (!ac) return
  const now = ac.currentTime
  for (const n of notes) {
    const start = now + (n.at ?? 0)
    const osc = ac.createOscillator()
    const gain = ac.createGain()
    osc.type = n.type ?? "sine"
    osc.frequency.setValueAtTime(n.from, start)
    osc.frequency.exponentialRampToValueAtTime(n.to, start + n.dur)
    // Fast attack, exponential release — reads as a tap, not a beep.
    gain.gain.setValueAtTime(0.0001, start)
    gain.gain.exponentialRampToValueAtTime(n.peak, start + 0.006)
    gain.gain.exponentialRampToValueAtTime(0.0001, start + n.dur)
    osc.connect(gain).connect(ac.destination)
    osc.start(start)
    osc.stop(start + n.dur + 0.02)
  }
}

/** Soft bright tick — buttons, chips, links. */
export function playTap() {
  play([
    { from: 1500, to: 900, dur: 0.07, peak: 0.12, type: "sine" },
    // A whisper of a lower body under the tick so it isn't thin on phones.
    { from: 520, to: 420, dur: 0.05, peak: 0.05, type: "triangle" },
  ])
}

/** Lower two-note pop — entering the search box. */
export function playFocus() {
  play([
    { from: 620, to: 660, dur: 0.06, peak: 0.09, type: "sine" },
    { from: 880, to: 940, dur: 0.09, peak: 0.08, type: "sine", at: 0.045 },
  ])
}
